import React, { useState, useEffect } from 'react';
import {
  Plus, Search, Filter, DollarSign, Calendar, Edit3, Trash2,
  Upload, Eye, X, FileText, ChevronUp, ChevronDown
} from 'lucide-react';
import api from '../../services/api';

interface Expense {
  id: number;
  expense_date: string;
  category: string;
  description: string;
  amount: number;
  currency: string;
  payment_method: string;
  paid_by: string;
  receipt_s3_key: string | null;
  created_by: string;
  created_at: string;
}

interface Summary {
  total_idr: number;
  total_inr: number;
  this_month_idr: number;
  this_month_inr: number;
  total_count: number;
  category_totals: Record<string, number>;
}

const CATEGORIES = [
  "Rent", "Utilities", "Salaries", "Travel",
  "Software", "Petty Cash", "Insurance",
  "Office Supplies", "Training", "Maintenance",
  "Incentives", "Bonus", "WiFi", "Other"
];

const PAYMENT_METHODS = ["Bank Transfer", "Cash", "Card", "UPI", "Other"];

type SortKey = 'expense_date' | 'category' | 'description' | 'amount' | 'payment_method' | 'paid_by';

const CompanyExpensesDashboard: React.FC = () => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' } | null>(null);
  const [filters, setFilters] = useState({
    category: '',
    currency: '',
    payment_method: '',
    paid_by: '',
    from_date: '',
    to_date: '',
  });

  const [form, setForm] = useState({
    expense_date: new Date().toISOString().split('T')[0],
    category: '',
    description: '',
    amount: '',
    currency: 'IDR',
    payment_method: '',
    paid_by: '',
  });

  const formatCurrency = (amount: number, currency: string) => {
    if (currency === 'IDR') {
      return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount);
    }
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amount);
  };

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const response = await api.get(`/company-expenses/?${params.toString()}`);
      if (response.data.success) setExpenses(response.data.data);
    } catch (error) {
      console.error('Error loading expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      const response = await api.get(`/company-expenses/summary?${params.toString()}`);
      if (response.data.success) setSummary(response.data.data);
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
      };

      let expenseId: number;
      if (editingExpense) {
        await api.put(`/company-expenses/${editingExpense.id}`, payload);
        expenseId = editingExpense.id;
      } else {
        const res = await api.post('/company-expenses/', payload);
        expenseId = res.data.data.id;
      }

      if (receiptFile) {
        const formData = new FormData();
        formData.append('file', receiptFile);
        await api.post(`/company-expenses/${expenseId}/upload-receipt`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      setShowModal(false);
      setEditingExpense(null);
      setReceiptFile(null);
      resetForm();
      loadExpenses();
      loadSummary();
    } catch (error) {
      console.error('Error saving expense:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await api.delete(`/company-expenses/${id}`);
      loadExpenses();
      loadSummary();
    } catch (error) {
      console.error('Error deleting expense:', error);
    }
  };

  const handleUploadReceipt = async (expenseId: number, file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/company-expenses/${expenseId}/upload-receipt`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      loadExpenses();
    } catch (error) {
      console.error('Error uploading receipt:', error);
    }
  };

  const handleViewReceipt = async (expenseId: number) => {
    try {
      const response = await api.get(`/company-expenses/${expenseId}/receipt-url`);
      if (response.data.success) {
        window.open(response.data.data.inline, '_blank');
      }
    } catch (error) {
      console.error('Error getting receipt URL:', error);
    }
  };

  const openEditModal = (expense: Expense) => {
    setEditingExpense(expense);
    setForm({
      expense_date: expense.expense_date,
      category: expense.category,
      description: expense.description || '',
      amount: expense.amount.toString(),
      currency: expense.currency,
      payment_method: expense.payment_method || '',
      paid_by: expense.paid_by || '',
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setForm({
      expense_date: new Date().toISOString().split('T')[0],
      category: '',
      description: '',
      amount: '',
      currency: 'IDR',
      payment_method: '',
      paid_by: '',
    });
  };

  useEffect(() => {
    loadExpenses();
    loadSummary();
  }, []);

  const handleSearch = () => {
    loadExpenses();
    loadSummary();
  };

  const handleSort = (key: SortKey) => {
    setSortConfig(prev => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const sortedExpenses = React.useMemo(() => {
    if (!sortConfig) return expenses;
    return [...expenses].sort((a, b) => {
      const { key, direction } = sortConfig;
      let aVal: any = a[key];
      let bVal: any = b[key];
      if (key === 'amount') {
        aVal = a.amount;
        bVal = b.amount;
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [expenses, sortConfig]);

  const SortIcon: React.FC<{ columnKey: SortKey }> = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) return <ChevronUp className="w-3 h-3 opacity-30" />;
    return sortConfig.direction === 'asc'
      ? <ChevronUp className="w-3 h-3" />
      : <ChevronDown className="w-3 h-3" />;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Company Expenses</h1>
            <p className="text-sm text-gray-500">Track office rent, utilities, salaries, and other company expenses</p>
          </div>
          <button
            onClick={() => { resetForm(); setEditingExpense(null); setShowModal(true); }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm"
          >
            <Plus className="w-4 h-4" /> Add Expense
          </button>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="mb-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <p className="text-sm text-gray-500">Total (IDR)</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(summary.total_idr, 'IDR')}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <p className="text-sm text-gray-500">Total (INR)</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(summary.total_inr, 'INR')}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <p className="text-sm text-gray-500">This Month (IDR)</p>
              <p className="text-xl font-bold text-blue-700">{formatCurrency(summary.this_month_idr, 'IDR')}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <p className="text-sm text-gray-500">This Month (INR)</p>
              <p className="text-xl font-bold text-blue-700">{formatCurrency(summary.this_month_inr, 'INR')}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">All</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Currency</label>
              <select
                value={filters.currency}
                onChange={(e) => setFilters({ ...filters, currency: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">All</option>
                <option value="IDR">IDR</option>
                <option value="INR">INR</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Payment Method</label>
              <select
                value={filters.payment_method}
                onChange={(e) => setFilters({ ...filters, payment_method: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">All</option>
                {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                value={filters.from_date}
                onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                value={filters.to_date}
                onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleSearch}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center justify-center gap-2"
              >
                <Search className="w-4 h-4" /> Search
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th onClick={() => handleSort('expense_date')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none">
                  <div className="flex items-center gap-1">Date <SortIcon columnKey="expense_date" /></div>
                </th>
                <th onClick={() => handleSort('category')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none">
                  <div className="flex items-center gap-1">Category <SortIcon columnKey="category" /></div>
                </th>
                <th onClick={() => handleSort('description')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none">
                  <div className="flex items-center gap-1">Description <SortIcon columnKey="description" /></div>
                </th>
                <th onClick={() => handleSort('amount')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none">
                  <div className="flex items-center gap-1">Amount <SortIcon columnKey="amount" /></div>
                </th>
                <th onClick={() => handleSort('payment_method')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none">
                  <div className="flex items-center gap-1">Payment <SortIcon columnKey="payment_method" /></div>
                </th>
                <th onClick={() => handleSort('paid_by')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none">
                  <div className="flex items-center gap-1">Paid By <SortIcon columnKey="paid_by" /></div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">Loading...</td>
                </tr>
              ) : sortedExpenses.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">No expenses found</td>
                </tr>
              ) : (
                sortedExpenses.map((expense) => (
                  <tr key={expense.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {new Date(expense.expense_date).toLocaleDateString('en-GB')}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700">
                        {expense.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                      {expense.description || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {formatCurrency(expense.amount, expense.currency)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {expense.payment_method || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {expense.paid_by || '-'}
                    </td>
                    <td className="px-4 py-3">
                      {expense.receipt_s3_key ? (
                        <button
                          onClick={() => handleViewReceipt(expense.id)}
                          className="text-blue-600 hover:text-blue-800"
                          title="View Receipt"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      ) : (
                        <label className="cursor-pointer text-gray-400 hover:text-gray-600" title="Upload Receipt">
                          <Upload className="w-4 h-4" />
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => {
                              if (e.target.files?.[0]) handleUploadReceipt(expense.id, e.target.files[0]);
                            }}
                          />
                        </label>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditModal(expense)}
                          className="text-gray-600 hover:text-blue-600"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(expense.id)}
                          className="text-gray-600 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingExpense ? 'Edit Expense' : 'Add Expense'}
              </h2>
              <button onClick={() => { setShowModal(false); setEditingExpense(null); }}>
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    value={form.expense_date}
                    onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">Select category</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Brief description"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                  <input
                    type="number"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency *</label>
                  <select
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="IDR">IDR</option>
                    <option value="INR">INR</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                  <select
                    value={form.payment_method}
                    onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">Select method</option>
                    {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Paid By</label>
                  <input
                    type="text"
                    value={form.paid_by}
                    onChange={(e) => setForm({ ...form, paid_by: e.target.value })}
                    placeholder="Name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Receipt</label>
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    {receiptFile ? receiptFile.name : 'Choose file'}
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                    />
                  </label>
                  {receiptFile && (
                    <button onClick={() => setReceiptFile(null)} className="text-red-500 text-xs hover:text-red-700">Remove</button>
                  )}
                  {editingExpense?.receipt_s3_key && !receiptFile && (
                    <span className="text-xs text-green-600">✓ Receipt uploaded</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <button
                onClick={() => { setShowModal(false); setEditingExpense(null); }}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!form.expense_date || !form.category || !form.amount}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingExpense ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyExpensesDashboard;
